import { PrivacyCurtain } from "@repo/ui/privacyCurtain/privacyCurtain";
import { usePrivacyCurtain } from "@repo/ui/privacyCurtain/usePrivacyCurtain";
import { IconBuilding } from "@tabler/icons-react";
import { useEffect, useState, type FC } from "react";
import { useAppSelector } from "../../hooks/hook.js";
import { selectCompany } from "../company/companySlice.js";
import { selectPrivacyCurtain } from "../teammate/teammateSlice.js";

/** How long the post-reveal workspace confirmation stays on screen. */
const REVEAL_CONFIRMATION_DURATION_MS = 6_000;

interface WorkspaceRevealConfirmationProps {
  companyName: string;
  onDismiss: () => void;
}

/**
 * Prominent, self-dismissing banner shown right after the curtain is lifted,
 * so the employee can confirm they are in the intended customer account.
 * This is the only place the flow shows the real customer name.
 */
const WorkspaceRevealConfirmation: FC<WorkspaceRevealConfirmationProps> = (
  props,
) => {
  const { companyName, onDismiss } = props;

  useEffect(() => {
    const timeoutId = window.setTimeout(
      onDismiss,
      REVEAL_CONFIRMATION_DURATION_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [onDismiss]);

  return (
    <div className="pointer-events-none fixed inset-x-0 top-6 z-[9998] flex justify-center">
      <div
        role="status"
        className="pointer-events-auto flex items-center gap-3 rounded-xl bg-slate-950 px-6 py-4 text-white shadow-2xl"
      >
        <IconBuilding aria-hidden className="size-6 shrink-0" />
        <div>
          <div className="text-xs font-medium tracking-wide text-slate-400 uppercase">
            You are viewing
          </div>
          <div className="text-xl font-semibold">{companyName}</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Screen-share privacy curtain for Alpharun employees viewing a customer
 * workspace, where `GET /user` turns it on: after the tab has been
 * backgrounded past the threshold, an opaque cover replaces the app (and the
 * tab title) until explicitly dismissed, and the curtain never shows the
 * customer's real name.
 */
export const EmployeePrivacyCurtain: FC = () => {
  const privacyCurtain = useAppSelector(selectPrivacyCurtain);
  const company = useAppSelector(selectCompany);

  const isEnabled = company !== null && privacyCurtain !== null;

  const { isCurtainActive, revealApp } = usePrivacyCurtain({
    isEnabled,
    maskedDocumentTitle: "Alpharun",
    // Unused while disabled.
    concealedThresholdMs: privacyCurtain?.concealed_threshold_ms ?? 0,
  });

  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

  if (!isEnabled) {
    return null;
  }

  if (isCurtainActive) {
    return (
      <PrivacyCurtain
        onReveal={() => {
          revealApp();
          setIsConfirmationVisible(true);
        }}
      />
    );
  }

  if (isConfirmationVisible) {
    return (
      <WorkspaceRevealConfirmation
        companyName={company.name}
        onDismiss={() => setIsConfirmationVisible(false)}
      />
    );
  }

  return null;
};
